/**
 * GiftCardRecommendations — reusable drop-in widget (Phase 2.8 frontend)
 *
 * Surfaces 2-3 contextual gift card "best picks" anywhere in the app.
 * Primary embed targets:
 *   • AI Concierge — after a Montreal package is generated (cross-sell).
 *   • Arrival Kit page — the digital gift card tile.
 *   • Future booking confirmation surfaces.
 *
 * Powered by the public, anonymous-accessible endpoint:
 *   GET /api/giftcards/recommendations?context=<intent>&limit=<n>&country=<iso3>
 *
 * Props (all optional):
 *   context   — free-form intent hint forwarded to the API (e.g. 'concierge:dining').
 *   limit     — how many cards to surface (default 3).
 *   country   — ISO-3 country filter (default 'CAN' — Montreal-first).
 *   title     — section heading (defaults to a Montreal-flavoured copy).
 *   subtitle  — sub-heading directly under the title.
 *   variant   — 'card' (default, bordered panel) | 'inline' (no chrome).
 *   onSelect  — optional callback fired with the brand when the user clicks
 *               a recommendation. If omitted, defaults to navigating to
 *               `/dining?brand=<canonical_id>` so the storefront opens the
 *               correct Buy drawer automatically.
 *
 * Render contract:
 *   - Renders nothing when the API returns zero items (no empty boxes
 *     in the host surface).
 *   - Skeletons during fetch so the layout doesn't shift.
 *   - Failures fail silently with a console warning — a cross-sell
 *     surface should never break the parent flow.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift, Sparkles, ChevronRight, ShieldCheck } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PINE = '#1F5C42';
const GOLD = '#FFC93C';
const PINK = '#FF5C8A';

const fmt = (amount, currency = 'USD') => {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)} ${currency || ''}`.trim();
  }
};

// Skeleton card kept structurally identical to the live tile so the
// layout doesn't reflow on load.
const SkeletonCard = () => (
  <div
    className="rounded-xl border border-gray-200 bg-white overflow-hidden animate-pulse"
    data-testid="giftcard-rec-skeleton"
  >
    <div className="aspect-[5/3] bg-gray-100" />
    <div className="p-3 space-y-2">
      <div className="h-3 w-2/3 bg-gray-200 rounded" />
      <div className="h-3 w-1/3 bg-gray-100 rounded" />
    </div>
  </div>
);

const RecommendationTile = ({ brand, onClick }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showLogo = brand.logo_url && !imgFailed;

  return (
    <button
      type="button"
      onClick={() => onClick(brand)}
      data-testid={`giftcard-rec-tile-${brand.canonical_id}`}
      className="group relative w-full text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2"
      style={{ '--tw-ring-color': PINK }}
    >
      {brand.is_featured && (
        <div
          className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
          style={{ background: GOLD, color: '#5C4400' }}
        >
          Featured
        </div>
      )}
      <div
        className="aspect-[5/3] flex items-center justify-center p-4"
        style={{ background: '#FAFAF7' }}
      >
        {showLogo ? (
          <img
            src={brand.logo_url}
            alt={brand.display_name}
            className="max-h-20 max-w-full object-contain"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Gift className="w-10 h-10" style={{ color: PINE, opacity: 0.5 }} />
        )}
      </div>
      <div className="p-3 border-t border-gray-100">
        <div className="flex items-center justify-between gap-2">
          <h4
            className="text-sm font-semibold text-gray-900 truncate"
            data-testid={`giftcard-rec-name-${brand.canonical_id}`}
          >
            {brand.display_name}
          </h4>
          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 flex-shrink-0 transition-colors" />
        </div>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-[10px] text-gray-500">from</span>
          <span
            className="text-sm font-bold"
            style={{ color: PINE }}
            data-testid={`giftcard-rec-price-${brand.canonical_id}`}
          >
            {fmt(brand.starting_at, brand.currency)}
          </span>
        </div>
      </div>
    </button>
  );
};

const GiftCardRecommendations = ({
  context,
  limit = 3,
  country = 'CAN',
  title = 'Save on your Montreal trip',
  subtitle = 'Pre-buy gift cards for restaurants, cafés and brands you love — up to 15% off face value.',
  variant = 'card',
  onSelect,
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const params = new URLSearchParams({
      limit: String(limit),
      country: country || 'CAN',
    });
    if (context) params.set('context', context);
    axios
      .get(`${API_BASE}/giftcards/recommendations?${params.toString()}`)
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
      })
      .catch((e) => {
        if (cancelled) return;
        // Cross-sell surfaces should never break the parent flow.
        console.warn('GiftCardRecommendations load failed', e?.message || e);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [context, limit, country]);

  const handleClick = (brand) => {
    if (typeof onSelect === 'function') {
      onSelect(brand);
      return;
    }
    navigate(`/dining?brand=${encodeURIComponent(brand.canonical_id)}`);
  };

  // Silent empty render so we don't leave a "no recommendations" stub.
  if (!loading && (failed || items.length === 0)) return null;

  const Container = variant === 'inline' ? 'div' : 'section';
  const containerClass =
    variant === 'inline'
      ? ''
      : 'rounded-2xl border border-gray-200 bg-white shadow-sm p-5';

  return (
    <Container
      data-testid="giftcard-recommendations"
      data-context={context || ''}
      className={containerClass}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: PINE }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Gift cards
          </div>
          <h3 className="mt-1 text-base sm:text-lg font-bold text-gray-900 leading-snug">
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-1 text-xs sm:text-sm text-gray-600 leading-relaxed">
              {subtitle}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => navigate('/dining')}
          data-testid="giftcard-recommendations-see-all"
          className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 flex-shrink-0 pt-1"
        >
          See all
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {loading
          ? Array.from({ length: limit }).map((_, i) => <SkeletonCard key={i} />)
          : items.map((b) => (
              <RecommendationTile
                key={b.canonical_id}
                brand={b}
                onClick={handleClick}
              />
            ))}
      </div>

      {!loading && (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: PINE }} />
          Delivered instantly to your Homesbyli Wallet.
        </div>
      )}
    </Container>
  );
};

export default GiftCardRecommendations;
